/**
 * Where the server is — the single answer, for every transport.
 *
 * In a browser the page and the API share an origin, so `location` is the right
 * answer and nothing here does any work. In a native shell they do not: the
 * WebView serves the bundle from `capacitor://localhost` (iOS) or
 * `https://localhost` (Android), so `location.host` is the *bundle*, not the
 * server, and anything derived from it connects to nothing.
 *
 * http.ts §4.1 already said to never hardcode a tailnet hostname because "Phase 4
 * depends on this being one value". It was one value for `fetch` only — the
 * WebSockets, the SSE streams and the /health probe each re-derived it from
 * `location`. Four transports, one of them honouring the rule. This module is
 * that rule made unavoidable.
 *
 * Found by avengents-app-builder while wiring the Capacitor shell, along with the
 * shape below; the two extra transports are theirs, not mine.
 *
 * All of it stays INERT until someone sets VITE_API_BASE: with it empty every
 * function returns exactly what the call sites built before, so the web app's
 * behaviour is unchanged rather than merely equivalent.
 */

/** Server origin, no trailing slash. Empty means "same origin as this page". */
export const API_BASE: string = (import.meta.env['VITE_API_BASE'] ?? '').replace(/\/+$/, '')

/**
 * True when the bundle and the API are different origins — which is also exactly
 * when cookies stop being usable, since `wt_sec` is SameSite=Strict and the server
 * reads the vault session from `request.cookies` and nowhere else. Anything that
 * needs to branch on "can I still rely on cookies?" should ask this, so that the
 * answer is derived rather than assumed a second time.
 */
export const IS_CROSS_ORIGIN: boolean = API_BASE !== ''

/** Absolute URL for an API path (`/health`, `/chat/events`, …). */
export function httpUrl(path: string): string {
  return `${API_BASE}${path}`
}

/** Absolute ws:// or wss:// URL for a socket path (`/ws`, `/stt/stream`, …). */
export function wsUrl(path: string): string {
  if (!API_BASE) {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${proto}//${location.host}${path}`
  }
  // https→wss, http→ws. The scheme is the only part that differs.
  return `${API_BASE.replace(/^http/, 'ws')}${path}`
}
